import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { GraphiQL } from 'graphiql';
import { createGraphiQLFetcher } from '@graphiql/toolkit';
import 'graphiql/graphiql.css';
import styled from 'styled-components';
import debounce from 'lodash/debounce';
import classNames from 'classnames';
import { Controller, useForm } from 'react-hook-form';
import { ajvResolver } from '@hookform/resolvers/ajv';
import { fullFormats } from 'ajv-formats/dist/formats';

import { useDeepCompareMemo } from '@clatter/platform';
import { config } from '../../../../../config';
import { getLocalStorageAccessToken } from '../../../../../helpers/auth';

import { NgErrorMessage } from '../../../../UI';
import { FolderManagerDynamicForm } from '../../../../FolderManager';

interface Props {
  query?: WorkspaceQuery;
  workspaceData: ProjectTemplate;
  isLoading?: boolean;
  onAction: (action: NgAction<{ display_name: string; query: string } | null>) => void;
}

const StyledGQLContainer = styled.div`
  height: 700px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;

  &.error {
    .graphiql-container {
      border: 1px solid ${({ theme }) => theme.palette.error};
      border-radius: ${({ theme }) => theme.radius.small};

      margin-bottom: ${({ theme }) => theme.spacing.small};
    }
  }

  &.disabled {
    .graphiql-container {
      pointer-events: none;
      opacity: 0.8;
    }
  }

  .graphiql-container {
    --color-base: 0, 0%, 100%;
    --color-primary: 195, 100%, 50%;
    --color-neutral: 0, 0%, 9%;

    background-color: transparent;
  }

  .graphiql-sessions {
    background-color: hsl(0, 0%, 98%) !important;
  }

  .graphiql-plugin {
    background-color: white;

    border-radius: 20px;

    margin: 16px 0;
  }

  .graphiql-sidebar {
    margin: 8px 0;
  }
`;

const formSchema = {
  type: 'object',
  properties: {
    display_name: {
      type: 'string',
      minLength: 1,
      errorMessage: {
        minLength: 'This field is required!',
      },
    },
    query: {
      type: 'string',
      minLength: 1,
      errorMessage: {
        minLength: 'This field is required!',
      },
    },
  },
  required: ['display_name', 'query'],
};

export const WorkspaceTemplateQueriesForm: FC<Props> = ({
  workspaceData,
  onAction,
  query,
  isLoading: isParentLoading,
}) => {
  const { ID: workspaceId } = workspaceData || {};

  const isLoading = !!isParentLoading;

  // keep GraphiQL query state separate from the form state
  // to avoid cursor glitches and re-render issues during editing
  const [queryString, setQueryString] = useState('');

  const customFormMethods = useForm({
    mode: 'onChange',
    resolver: ajvResolver(formSchema as any, {
      formats: fullFormats,
    }),
  });

  const {
    control,
    setValue,
    reset,
    formState: { errors },
  } = customFormMethods;

  const formFields = useDeepCompareMemo(() => {
    return [
      {
        name: 'display_name',
        type: 'string',
        label: 'Display Name',
      },
    ];
  }, [workspaceData]);

  const initialValues = useDeepCompareMemo(
    () => ({
      display_name: query?.DisplayName,
      query: query?.Query || '',
    }),
    [workspaceData, query],
  );

  const fetcher = useMemo(() => {
    return createGraphiQLFetcher({
      url: `${config.assetServiceBaseUrl}/v2/project-templates/${workspaceId}/graphql`,
      headers: {
        Authorization: `Bearer ${getLocalStorageAccessToken()}`,
      },
    });
  }, [workspaceId]);

  const handleDynamicFormAction = (data: { display_name: string; query: string } | 'cancel') => {
    if (data === 'cancel') {
      onAction({ type: 'CANCEL_EDIT', payload: null });
      return;
    }

    onAction({ type: 'UPDATE', payload: data });
  };

  const setQueryDebounced = useCallback(
    debounce((value) => {
      setValue('query', value, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
    }, 300),
    [setValue],
  );

  useEffect(() => {
    setQueryString(initialValues.query);
    reset(initialValues);
  }, [initialValues]);

  useEffect(() => {
    setQueryDebounced(queryString);
  }, [queryString, setQueryDebounced]);

  useEffect(() => {
    return () => {
      setQueryDebounced.cancel();
    };
  }, [setQueryDebounced]);

  return (
    <>
      <FolderManagerDynamicForm
        formSchema={{ fields: formFields, validation: formSchema }}
        customFormMethods={customFormMethods}
        onAction={handleDynamicFormAction}
        buttonsTargetContainerId="header-form-buttons"
        isLoading={isLoading}
        minWidth="100%"
        includeCancelButton
      >
        <Controller
          name="query"
          control={control}
          render={() => {
            return (
              <>
                <StyledGQLContainer className={classNames({ error: errors?.['query']?.message })}>
                  <GraphiQL
                    key={query?.ID || 'new'}
                    fetcher={fetcher}
                    query={queryString}
                    onEditQuery={(newQuery) => setQueryString(newQuery || '')}
                    storage={null}
                    {...({ forcedTheme: 'light' } as any)}
                  />

                  {errors?.['query']?.message && <NgErrorMessage message={errors['query'].message as string} />}
                </StyledGQLContainer>
              </>
            );
          }}
        />
      </FolderManagerDynamicForm>

      <div id="header-form-buttons"></div>
    </>
  );
};
